import React, { useState, useEffect } from "react";
import MultiInput from "components/input/multi";
import { RiAddFill, RiPencilRuler2Line, RiClipboardLine, RiCheckboxMultipleFill, RiCheckboxFill, RiCheckboxBlankLine, RiCheckboxCircleFill } from "react-icons/ri";
import { IconButton, OutlineButton, Button } from "components/button";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { dollarFormatter } from "utils";
import { cloneDeep } from "lodash";
import Assignee from "components/react-select/assignees";
import DollarInputV2 from "components/input/dollarInputV2";
import { useSnackbarContext } from "components/snackbar";
import { useTranslation } from 'react-i18next';
import { useModalContext } from "components/modal";

type Props = {
  id: string;
  type: "request" | "project" | "invoice" | "customer";
  customerId?: string;
}

type SelectedOption = {
  id: string;
  label: string;
  estimate_id: string;
  estimate_label: string;
  price_type: "sale" | "install";
  amount?: number;
};

type IncomingOptions = SelectedOption & {
  selected?: boolean; 
  actual_total: number;
};

type DepositRequestForm = {
  customerId: string | undefined;
  memo: string | null;
  customText: string | null;
  total: number | null;
  emails: (string | null)[] | null;
  managerId: string | undefined;
  selectedOptions: SelectedOption[];
  depositAmount: number;
};

const DepositRequestContainer = ({ id, type, customerId }: Props) => {
  const { t } = useTranslation();
  const tBase = "views.payment.components.DepositRequestContainer";
  const tr = (key: string) => t(`${tBase}.${key}`)

  const snackbar = useSnackbarContext();
  const { setModal } = useModalContext();
  const queryClient = useQueryClient();

  const [emails, setEmails] = useState<(string | null)[] | null>([]);
  const [isMultiple, setIsMultiple] = useState(false);
  const [customText, setCustomText] = useState<string>("");
  const [memo, setMemo] = useState<string>("");
  const [manager, setManager] = useState<{
      value: string;
      label: string;
    } | null>(null);
  const [selected, setSelected] = useState<SelectedOption[]>([]);
  const [depositAmount, setDepositAmount] = useState<number | null>(null);

  const { data: optionsData } = useQuery<{
    parent: { actual_total: number; price_type: "sale" | "install" } | null;
    options: IncomingOptions[]; // expand this to include selected options
  }>(
    ["creating invoice", id, type],
    async () => await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/estimates/${id}/options?type=${type}`
      ).then((res) => res.data)
  );

  const mutation = useMutation(
    async (e: DepositRequestForm) =>
      await axios
        .post(
          `
            ${process.env.REACT_APP_SERVER_URL}/api/v1/company/deposit-request?type=${type}&id=${id}
          `,
          e
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        snackbar.showSnackbar(tr("Deposit request sent!"));
        setModal();
        queryClient.invalidateQueries();
      },
      onError: (error: any) => {
        snackbar.showSnackbar(tr("An error occurred while sending the deposit request."));
      }
    }
  );

  const getCustomerEmails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers/${id}/emails`,
        { params: { type } }
      );
      return response.data || []; 
    } catch (error) {
      console.error("Error fetching customer emails:", error);
      return [];
    }
  };

  useEffect(() => {
    if (customerId) {
      getCustomerEmails().then((emails) => {
        setEmails(emails || []); 
      });
    }
  }, [customerId]);

  const handleSelectOption = (option: any) => {
    let newSelected: SelectedOption[];

    if (isMultiple) {
      const already = selected.find(s => s.id === option.id);

      if (already) {
        // remove it
        newSelected = selected.filter(s => s.id !== option.id);
      } else {
        // enforce same price_type
        if (selected.length > 0 && selected[0].price_type !== option.price_type) {
          return; 
        }
        newSelected = [
          ...selected,
          { 
            id: option.id, 
            label: option.label, 
            estimate_id: option.estimate_id,
            estimate_label: option.estimate_label,
            price_type: option.price_type, 
            amount: option.actual_total 
          }
        ];
      }
    } else {
      // single‐select mode
      newSelected = [
        { 
          id: option.id, 
          label: option.label, 
          estimate_id: option.estimate_id,
          estimate_label: option.estimate_label,
          price_type: option.price_type, 
          amount: option.actual_total 
        }
      ];
    }
    
    setSelected(newSelected);

    const totalCents = newSelected.reduce((sum, s) => sum + (s.amount ?? 0), 0);
    const totalDollars = totalCents / 100;
    setDepositAmount(totalDollars);
  };

  return (
    <div className="w-[calc(100vw-2rem)] flex flex-col space-y-4 pb-4 pl-[1px] max-w-[600px]">
      <div>
        <div>{tr("Email")}</div>
        <MultiInput values={emails} onChange={(e) => setEmails(e)} />
      </div>

      {/* Estimate Selection */}
      <div>
        <div className="flex justify-between items-end">

          <div>{tr("Estimate Options")}</div>
          <div>
            <IconButton
              size="small"
              onClick={() => {
                setIsMultiple((s) => !s);
                setSelected([]);
              }}
              className={`${isMultiple && "!bg-[#ff4f00] !text-white"}`}
            >
              <RiCheckboxMultipleFill />
            </IconButton>
          </div>
        </div>

        {/* Estimate Options */}
        <div className="flex flex-col space-y-1">
          {optionsData?.options?.map((option) => {
            if (!option) return null;

            const isSelected = selected.some((sel) => sel.id === option.id);

            const disabled = isMultiple && selected.length > 0 && selected[0].price_type !== option.price_type;
            
            return (
              <button 
                key={option.label}
                className={`flex justify-between items-center border p-4 min-h-16 space-x-2 rounded-md 
                  ${isSelected && " border-green-600"}
                  ${disabled && " border-red-600 bg-gray-100 text-gray-500 cursor-not-allowed"}`
                }
                onClick={() => handleSelectOption(option)}
                disabled={disabled}
              >
                <div className="flex items-center space-x-2"> 
                  <div className="flex items-center space-x-2">
                    {isMultiple &&
                      (isSelected ? (
                        <RiCheckboxFill className="text-green-600 h-5 w-5" />
                      ) : (
                        <RiCheckboxBlankLine
                          className={`h-5 w-5 ${disabled ? "text-red-500" : "text-slate-500"}`}
                        />
                    ))}
                    <div className="rounded-full bg-[#ff4f00]/10 text-[1.2rem] !text-[#ff4f00] h-8 w-8 flex justify-center items-center">
                      <RiClipboardLine />
                    </div>
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="text-[0.9rem] font-bold text-left">#{option.id} {option.label} | {dollarFormatter(option.actual_total || 0)}</span>
                    <span className="text-slate-500 font-semibold text-xs text-left">
                      <span>
                        {`${option.estimate_label}`}
                        {option.selected && (
                          <span className="mx-2 bg-green-200 px-2 text-black font-bold">
                            {tr("Won")}
                          </span>
                        )}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div>{option.price_type}</div>
                  {!isMultiple && isSelected && (
                    <RiCheckboxCircleFill className=" ml-auto !text-2xl !text-green-600" />
                  )}
                </div>
              </button>
            )
          }
          )}
        </div>
      </div>

      {/* Memo */}
      <div className="flex flex-col">
        <div>
          <span>{tr("Memo")}</span>
          <span className="ml-1 text-xs text-gray-500">({tr("For internal records")})</span>
        </div>
        <textarea
          className="border border-slate-300 rounded-md p-2 min-h-[40px] focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary"
          placeholder={tr("Memo for the deposit request")}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          rows={1}
          minLength={10}
        />
      </div>

      {/* Custom Text */}
      <div className="flex flex-col">
        <div>
          <span>{tr("Custom Text")}</span>
          <span className="ml-1 text-slate-500 text-xs">({tr("To be included in the email")})</span>
        </div>
        <textarea
          className="border border-slate-300 rounded-md p-2 min-h-[80px] focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary"
          placeholder={tr("Add custom text for the deposit request")}
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          rows={3}
          minLength={10}
        />
      </div>

      {/* Manager Selection */}
      <div>
        <div>
          <span>{tr("Manager")}</span>
          <span className="ml-1 text-slate-500 text-xs">({tr("Optional")})</span>
        </div>
        <Assignee
          value={manager}
          isMulti={false}
          // menuPlacement="top"
          onChange={(e: any) => setManager(e)}
        />
      </div>

      {/* Deposit Amount */}
      <div>
        <div>{tr("Deposit Amount")}</div>
        <DollarInputV2
          required
          name="total"
          value={depositAmount ?? "0"}
          onValueChange={({ floatValue }) => setDepositAmount(floatValue ?? null)}
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-end space-x-2 mt-4">
        <OutlineButton onClick={() => setModal()}>
          {tr("Cancel")}
        </OutlineButton>
        <Button
          primary
          disabled={!depositAmount || depositAmount <= 0 || !emails?.length || selected.length === 0}
          onClick={() => {
            mutation.mutate({
              customerId: customerId,
              memo: memo || null,
              customText: customText || null,
              total: depositAmount || null,
              emails,
              managerId: manager?.value,
              selectedOptions: selected,
              depositAmount: Math.round((depositAmount ?? 0) * 100) // convert to cents
            });
          }}
        >
          {tr("Send")}
        </Button>
      </div>
    </div>
  );
};

export default DepositRequestContainer;